import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import "./WalkiesAndFoodPage.css";
import { ArrowLeft, Loader2 } from "lucide-react";
import nextIcon from "../../assets/nextButtonIcon.png";
import nextIcon2 from "../../assets/nextButtonIcon2.png";
import apiClient from "../../api/apiClient";
import TopNavBar from "../../components/TopNavBar";
import appIcon from "../../assets/socialPawsLogo.svg";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";

const WalkiesAndFoodPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.user);
  const [user, setUser] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const location = useLocation();
  const stateData = location.state || {};
  const partnerId = stateData.partnerId || null;

  const [mobileStep, setMobileStep] = useState(
    location.state?.returnToStep || 1,
  );

  const statusToIndex = {
    "Not yet": 0,
    "In progress": 1,
    "Mostly trained": 2,
    "Fully trained": 3,
  };

  useEffect(() => {
    const fetchAllData = async () => {
      if (!profile?.id) return;

      setPageLoading(true);

      try {
        // Fetch User
        const userRes = await apiClient.get(`/user/details/${profile.id}`);

        if (!userRes.data?.status || !userRes.data.data) {
          setPageLoading(false);
          return;
        }

        const userData = userRes.data.data;
        setUser(userData);

        const dogId = userData.dogs?.[0]?.id;
        if (!dogId) {
          setPageLoading(false);
          return;
        }

        // Fetch Playbook
        const playbookRes = await apiClient.get(
          `/pupCarePlaybook/get-pup-care-playbook-by-dogid/${dogId}`,
        );

        if (playbookRes.data?.status && playbookRes.data.data) {
          const pd = playbookRes.data.data;

          const v1 = pd.vaccinations?.[0] || "";
          const v2 = pd.vaccinations?.[1] || "";
          const v3 = pd.vaccinations?.[2] || "";

          setFormData({
            ...pd,
            vaccine1: v1,
            vaccine2: v2,
            vaccine3: v3,
            houseTrainingLevel: statusToIndex[pd.house_training_status] ?? 3,
            is_spayed: pd.is_spayed,
            is_microchipped: pd.is_microchipped,
          });
        }
      } catch (err) {
        console.error("Error loading page:", err);
      } finally {
        setPageLoading(false);
      }
    };

    fetchAllData();
  }, [profile?.id]);

  const myDog = user?.dogs?.[0]?.dog_name || "your dog";

  const handleWalkiesChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFoodChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Validation
  const isStep1Valid =
    (formData.walk_guidelines?.trim() || "") !== "" &&
    (formData.leash_guidelines?.trim() || "") !== "";

  const isStep2Valid =
    (formData.treatos?.trim() || "") !== "" &&
    (formData.allergies_and_restrictions?.trim() || "") !== "" &&
    (formData.meals?.trim() || "") !== "";

  const isFormValid = isStep1Valid && isStep2Valid;

  const handleNext = () => {
    if (window.innerWidth >= 1024) {
      if (!isFormValid) return;
      navigate("/pup-emergency-plan");
    } else {
      if (!isStep2Valid) return;
      navigate("/pup-emergency-plan");
    }
  };

  if (pageLoading) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(157.14deg, #96f2de 2.76%, #58b8a3 96.76%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
        <p style={{ marginTop: "12px", fontWeight: "600" }}>
          Fetching Playbook...
        </p>
      </div>
    );
  }

  return (
    <div
      className={`wf-container ${mobileStep === 1 ? "wf-mobile-step-1" : "wf-mobile-step-2"}`}
    >
      {/* LEFT PANEL (Walkies) - Top on Mobile / Step 1 */}
      <div
        className={`wf-panel wf-left-panel ${mobileStep === 1 ? "wf-mobile-active" : "wf-mobile-hidden"}`}
      >
        {/* Desktop Back Button */}
        <div
          className="login-back-btn wf-desktop-only"
          style={{ top: "1.5rem", marginLeft: "-14px", marginTop: "2px" }}
          onClick={() => navigate("/dashboard-switcher")}
        >
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "28px", height: "28px", objectFit: "contain" }}
          />
          <span
            style={{
              color: "rgba(0, 155, 206, 1)",
              fontSize: "22px",
              fontWeight: "700",
            }}
          >
            Social Paws
          </span>
        </div>

        {/* Mobile Header */}
        <div className="cb-custom-top-header cb-mobile-only" style={{background: "transparent"}}>
          {/* TOP ROW */}
          <div className="cb-header-top-row">
            <MobileTopBar
              onBack={() => navigate("/dashboard-switcher")}
              backColor="#000"
              logoColor="#009fd9"
              blueLogo={true}
            />
          </div>

          {/* BOTTOM ROW */}
          <div className="cb-header-bottom-row">
            <div className="header-icons-right">
              <TopNavBar theme="light" />
            </div>
          </div>
        </div>

        <div className="wf-content-wrapper">
          <h1 className="wf-title">WALKIES</h1>
          <p className="wf-subtitle">
            SHARE {myDog.toUpperCase()}'S WALKIE PREFERENCES
          </p>

          <div className="wf-input-group">
            <label className="wf-label-dark">WALK GUIDELINES</label>
            <textarea
              name="walk_guidelines"
              className="wf-textarea-light"
              placeholder="Favorite routes, areas to avoid, preferred times..."
              value={formData.walk_guidelines || ""}
              onChange={handleWalkiesChange}
            />
          </div>

          <div className="wf-input-group">
            <label className="wf-label-dark">LEASH GUIDELINES</label>
            <textarea
              name="leash_guidelines"
              className="wf-textarea-light"
              placeholder="Pulls on leash, good with other dogs, reactive to squirrels..."
              value={formData.leash_guidelines || ""}
              onChange={handleWalkiesChange}
            />
          </div>

          <div className="wf-input-group">
            <label className="wf-label-dark">WHAT ELSE?</label>
            <textarea
              name="walkies_other_notes"
              className="wf-textarea-light"
              placeholder="Share anything and everything to help your pup pal take the best care of your furry friend!"
              value={formData.walkies_other_notes || ""}
              onChange={handleWalkiesChange}
            />
          </div>

          {/* Mobile Only: Next Button for Step 1 */}
          {/* <div className="wf-mobile-footer wf-mobile-only wf-step-1-next">
            <button
              className={`wf-next-btn-group ${isStep1Valid ? "enabled" : "disabled"}`}
              onClick={() => isStep1Valid && setMobileStep(2)}
            >
              <span className="wf-next-text" style={{ color: "#009fd9" }}>
                NEXT
              </span>
              <div
                className="wf-next-circle"
                style={{ backgroundColor: "rgba(0, 159, 217, 0.15)" }}
              >
                <img src={nextIcon2} alt="Next" className="wf-next-icon" />
              </div>
            </button>
          </div> */}
          <MobileBottomBar
            onNext={() => isStep1Valid && setMobileStep(2)}
            isNextEnabled={isStep1Valid}
            nextIconSrc={nextIcon2}
            variant="blue"
          />
        </div>
      </div>

      {/* RIGHT PANEL (Food) - Bottom on Mobile / Step 2 */}
      <div
        className={`wf-panel wf-right-panel ${mobileStep === 2 ? "wf-mobile-active" : "wf-mobile-hidden"}`}
      >
        {/* Desktop Top Navbar */}
        <div className="wf-top-navbar wf-desktop-only">
          <TopNavBar theme="dark" />
        </div>

        {/* Mobile Header (Hidden on Desktop) */}
        {/* <div className="wf-mobile-header wf-mobile-only">
          <button className="wf-back-btn" onClick={() => setMobileStep(1)}>
            <ArrowLeft size={22} color="#fff" strokeWidth={3} />
            <span style={{ color: "#fff" }}>Back</span>
          </button>
          <div className="wf-header-logo" style={{ color: "#fff" }}>
            <img src={appIcon} alt="Social Paws" className="wf-logo-img" />
            <span>Social Paws</span>
          </div>
        </div> */}
        <div className="cb-custom-top-header cb-mobile-only" style={{background: "transparent"}}>
          {/* TOP ROW */}
          <div className="cb-header-top-row">
            <MobileTopBar
              onBack={() => setMobileStep(1)}
              backColor="#fff"
              logoColor="#fff"
            />
          </div>

          {/* BOTTOM ROW */}
          <div className="cb-header-bottom-row">
            <div className="header-icons-right">
              <TopNavBar theme="dark" />
            </div>
          </div>
        </div>

        <div className="wf-content-wrapper wf-right-content">
          <h1 className="wf-title wf-text-white">FOOD</h1>
          <p className="wf-subtitle wf-text-white">LET'S TALK MEALS & TREATS</p>

          <div className="wf-input-group">
            <label className="wf-label-white">TREATOS</label>
            <textarea
              name="treatos"
              className="wf-textarea-blue"
              placeholder={`What does ${myDog} have the green light to enjoy?`}
              value={formData.treatos || ""}
              onChange={handleFoodChange}
            />
          </div>

          <div className="wf-input-group">
            <label className="wf-label-white">ALLERGIES & RESTRICTIONS</label>
            <textarea
              name="allergies_and_restrictions"
              className="wf-textarea-blue"
              placeholder={`What should ${myDog}'s pup pal be careful to limit or avoid?`}
              value={formData.allergies_and_restrictions || ""}
              onChange={handleFoodChange}
            />
          </div>

          <div className="wf-input-group">
            <label className="wf-label-white">MEALS</label>
            <textarea
              name="meals"
              className="wf-textarea-blue"
              placeholder={`How much, how often, and what do you want ${myDog} fed?`}
              value={formData.meals || ""}
              onChange={handleFoodChange}
            />
          </div>

          <div className="wf-input-group">
            <label className="wf-label-white">WHAT ELSE?</label>
            <textarea
              name="food_other_notes"
              className="wf-textarea-blue"
              placeholder="Share anything and everything to help your pup pal take the best care of your furry friend!"
              value={formData.food_other_notes || ""}
              onChange={handleFoodChange}
            />
          </div>

          {/* Footer Navigation (Desktop + Mobile Step 2) */}
          <div className="wf-footer wf-step-2-next desktop-only">
            <button
              className={`wf-next-btn-group ${
                window.innerWidth >= 1024
                  ? isFormValid
                    ? "enabled"
                    : "disabled"
                  : isStep2Valid
                    ? "enabled"
                    : "disabled"
              }`}
              onClick={handleNext}
            >
              <span
                className="wf-next-text"
                style={window.innerWidth < 1024 ? { color: "#47d7ac" } : {}}
              >
                NEXT
              </span>
              <div
                className="wf-next-circle"
                style={
                  window.innerWidth < 1024
                    ? { backgroundColor: "rgba(71, 215, 172, 0.15)" }
                    : {}
                }
              >
                <img src={nextIcon} alt="Next" className="wf-next-icon" />
              </div>
            </button>
          </div>
          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={isStep2Valid}
            nextIconSrc={nextIcon}
            variant="green"
          />
        </div>
      </div>
    </div>
  );
};

export default WalkiesAndFoodPage;
